/**
 * Submission files — the Files section's Uploaded Files tab.
 *
 * Backed by the real `/v2/submissions/{id}/files/` API:
 *   GET    /v2/submissions/:id/files/          → { total, items[] }
 *   POST   /v2/submissions/:id/files/          multipart (field "file")
 *   DELETE /v2/submissions/:id/files/:fileId
 *
 * Always hits the real backend (`*Real` helpers) — never mocked. The
 * list item carries a presigned `download_url` and `uploaded_by` as a
 * numeric user id (name resolution is pending a backend users endpoint —
 * the FE renders the id best-effort until then).
 */
import { getJsonReal, sendJsonReal, uploadMultipartReal } from "../client";

function filesBase(submissionId: string): string {
    return `/v2/submissions/${encodeURIComponent(submissionId)}/files`;
}

/** Wire row from the files list. */
/** The uploader's user record (relationship expanded on the file row). */
interface UploadedByUserWire {
    id: number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
}

interface SubmissionFileWire {
    id: string;
    name: string;
    content_type: string | null;
    size_bytes: number | null;
    uploaded_at: string;
    uploaded_by: number | null;
    uploaded_by_user: UploadedByUserWire | null;
    download_url: string | null;
}

interface SubmissionFilesResponseWire {
    total: number;
    items: SubmissionFileWire[];
}

/** FE model for an uploaded file row. */
export interface SubmissionFile {
    id: string;
    name: string;
    contentType: string;
    sizeBytes: number;
    /** ISO timestamp. */
    uploadedAt: string;
    /** Numeric backend user id. */
    uploadedBy: number | null;
    /** Uploader's display name (from `uploaded_by_user`), or null. */
    uploadedByName: string | null;
    /** Uploader's initials for the avatar (e.g. "DE"). */
    uploadedByInitials: string;
    downloadUrl: string;
}

/** Full name → email → null, from the expanded uploader record. */
function uploaderName(u: UploadedByUserWire | null): string | null {
    if (!u) return null;
    return [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || `User #${u.id}`;
}

/** First+last initials → email initial → "?". */
function uploaderInitials(u: UploadedByUserWire | null): string {
    if (!u) return "?";
    const f = (u.first_name ?? "").trim();
    const l = (u.last_name ?? "").trim();
    const fromName = ((f[0] ?? "") + (l[0] ?? "")).toUpperCase();
    if (fromName) return fromName;
    const e = (u.email ?? "").trim();
    return e ? e[0].toUpperCase() : "?";
}

function toFile(w: SubmissionFileWire): SubmissionFile {
    return {
        id: w.id,
        name: w.name,
        contentType: w.content_type ?? "",
        sizeBytes: w.size_bytes ?? 0,
        uploadedAt: w.uploaded_at,
        uploadedBy: w.uploaded_by,
        uploadedByName: uploaderName(w.uploaded_by_user),
        uploadedByInitials: uploaderInitials(w.uploaded_by_user),
        downloadUrl: w.download_url ?? "",
    };
}

export function listSubmissionFiles(submissionId: string): Promise<SubmissionFile[]> {
    return getJsonReal<SubmissionFilesResponseWire>(`${filesBase(submissionId)}/`).then((res) =>
        res.items.map(toFile),
    );
}

export function uploadSubmissionFile(submissionId: string, file: File): Promise<SubmissionFile> {
    const form = new FormData();
    form.append("file", file);
    return uploadMultipartReal<SubmissionFileWire>(`${filesBase(submissionId)}/`, form).then(toFile);
}

export function deleteSubmissionFile(submissionId: string, fileId: string): Promise<void> {
    return sendJsonReal<void>(`${filesBase(submissionId)}/${encodeURIComponent(fileId)}`, "DELETE");
}
